import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  CircularProgress,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  Tooltip,
  LinearProgress,
} from '@mui/material';
import {
  TrendingUp,
  Assessment,
  Psychology,
  Calculate,
  Inventory,
  PeopleAlt,
  AttachMoney,
  ShowChart,
  DataUsage,
  Refresh,
} from '@mui/icons-material';
import {
  LineChart,
  Line,
  AreaChart,
  Area,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import axios from 'axios';

const Analytics = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [analytics, setAnalytics] = useState({
    predictions: [],
    estimations: [],
    inventory: [],
    trainingData: [],
    summary: {
      totalPredictions: 0,
      totalEstimations: 0,
      averageAccuracy: 0,
      totalRevenue: 0,
    }
  });

  useEffect(() => {
    fetchAnalytics();
  }, []);

  const fetchAnalytics = async () => {
    try {
      setLoading(true);
      const [predictionsRes, estimationsRes, inventoryRes, trainingRes] = await Promise.all([
        axios.get('/api/get-predictions/'),
        axios.get('/api/estimations/'),
        axios.get('/api/add-batch/'),
        axios.get('/api/analytics/')
      ]);

      const predictions = predictionsRes.data.predictions || [];
      const estimations = estimationsRes.data.estimations || [];
      const inventory = inventoryRes.data.inventory || [];
      const trainingData = trainingRes.data.training_data || [];

      // Calculate summary metrics
      const totalRevenue = estimations.reduce((sum, est) => sum + parseFloat(est.total_estimation || 0), 0);
      const averageAccuracy = trainingData.length > 0 
        ? trainingData.reduce((sum, data) => {
            const accuracy = Math.max(0, 100 - Math.abs((data.estimated - data.actual) / data.actual) * 100);
            return sum + accuracy;
          }, 0) / trainingData.length
        : 0;

      setAnalytics({
        predictions,
        estimations,
        inventory,
        trainingData,
        summary: {
          totalPredictions: predictions.length,
          totalEstimations: estimations.length,
          averageAccuracy: averageAccuracy.toFixed(1),
          totalRevenue: totalRevenue.toFixed(2),
        }
      });
      
      setError('');
    } catch (error) {
      console.error('Error fetching analytics:', error);
      setError('Failed to load analytics data');
    } finally {
      setLoading(false);
    }
  };

  // Prepare chart data
  const predictionTrends = analytics.predictions.slice(-10).map((pred, index) => ({
    name: `Prediction ${index + 1}`,
    guests: pred.guests,
    location: pred.location,
    menuItems: Object.keys(pred.menu_items || {}).length,
    totalQuantity: Object.values(pred.menu_items || {}).reduce((sum, qty) => sum + qty, 0)
  }));

  const revenueTrends = analytics.estimations.slice(-10).map((est, index) => ({
    name: `Est ${index + 1}`,
    estimated: parseFloat(est.total_estimation || 0),
    taken: parseFloat(est.amount_taken || 0),
    profit: parseFloat(est.total_estimation || 0) - parseFloat(est.amount_taken || 0)
  }));

  const inventoryStatus = analytics.inventory.reduce((acc, item) => {
    const existing = acc.find(a => a.status === item.status);
    if (existing) {
      existing.count += 1;
    } else {
      acc.push({ status: item.status, count: 1 });
    }
    return acc;
  }, []);

  const menuDistribution = analytics.predictions.reduce((acc, pred) => {
    const existing = acc.find(a => a.name === pred.menu_name);
    if (existing) {
      existing.value += 1;
    } else {
      acc.push({ name: pred.menu_name, value: 1 });
    }
    return acc;
  }, []);

  const COLORS = ['#1976d2', '#42a5f5', '#64b5f6', '#90caf9', '#bbdefb'];

  const StatCard = ({ title, value, icon, color, subtitle }) => (
    <motion.div
      initial={{ opacity: 0, scale: 0.9 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ duration: 0.3 }}
    >
      <Card className="card-hover" sx={{ height: '100%' }}>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Box>
              <Typography color="text.secondary" gutterBottom variant="h6">
                {title}
              </Typography>
              <Typography variant="h4" component="div" sx={{ fontWeight: 700, color }}>
                {value}
              </Typography>
              {subtitle && (
                <Typography variant="body2" color="text.secondary">
                  {subtitle}
                </Typography>
              )}
            </Box>
            <Box
              sx={{
                backgroundColor: `${color}15`,
                borderRadius: 2,
                p: 2,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              {icon}
            </Box>
          </Box>
        </CardContent>
      </Card>
    </motion.div>
  );

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
        <CircularProgress size={48} />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
          <Typography
            variant="h4"
            component="h1"
            sx={{
              fontWeight: 700,
              background: 'linear-gradient(45deg, #1976d2, #42a5f5)',
              backgroundClip: 'text',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              display: 'flex',
              alignItems: 'center',
              gap: 2
            }}
          >
            <Assessment sx={{ fontSize: 'inherit', color: '#1976d2' }} />
            Analytics Dashboard
          </Typography>
          
          <Tooltip title="Refresh data">
            <IconButton onClick={fetchAnalytics} color="primary">
              <Refresh />
            </IconButton>
          </Tooltip>
        </Box>
      </motion.div>

      {error && (
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.3 }}
        >
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        </motion.div>
      )}

      {/* Summary Stats */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Total Predictions"
            value={analytics.summary.totalPredictions}
            icon={<Psychology sx={{ fontSize: 32, color: '#1976d2' }} />}
            color="#1976d2"
            subtitle="AI predictions made"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Cost Estimations"
            value={analytics.summary.totalEstimations}
            icon={<Calculate sx={{ fontSize: 32, color: '#2e7d32' }} />}
            color="#2e7d32"
            subtitle="Cost calculations"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Prediction Accuracy"
            value={`${analytics.summary.averageAccuracy}%`}
            icon={<TrendingUp sx={{ fontSize: 32, color: '#ed6c02' }} />}
            color="#ed6c02"
            subtitle="Average accuracy"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Total Revenue"
            value={`PKR ${analytics.summary.totalRevenue}`}
            icon={<AttachMoney sx={{ fontSize: 32, color: '#9c27b0' }} />}
            color="#9c27b0"
            subtitle="Estimated revenue"
          />
        </Grid>
      </Grid>

      {/* Charts */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        {/* Prediction Trends */}
        <Grid item xs={12} md={6}>
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.2, duration: 0.5 }}
          >
            <Card className="card-hover">
              <CardContent>
                <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <ShowChart />
                  Prediction Trends
                </Typography>
                <ResponsiveContainer width="100%" height={300}>
                  <LineChart data={predictionTrends}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="name" />
                    <YAxis />
                    <RechartsTooltip />
                    <Legend />
                    <Line type="monotone" dataKey="guests" stroke="#1976d2" strokeWidth={3} />
                    <Line type="monotone" dataKey="totalQuantity" stroke="#42a5f5" strokeWidth={2} />
                  </LineChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          </motion.div>
        </Grid>

        {/* Revenue Analysis */}
        <Grid item xs={12} md={6}>
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.3, duration: 0.5 }}
          >
            <Card className="card-hover">
              <CardContent>
                <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <AttachMoney />
                  Revenue Analysis
                </Typography>
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={revenueTrends}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="name" />
                    <YAxis />
                    <RechartsTooltip />
                    <Legend />
                    <Bar dataKey="estimated" fill="#2e7d32" />
                    <Bar dataKey="taken" fill="#ed6c02" />
                  </BarChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          </motion.div>
        </Grid>

        {/* Menu Distribution */}
        <Grid item xs={12} md={6}>
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4, duration: 0.5 }}
          >
            <Card className="card-hover">
              <CardContent>
                <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <DataUsage />
                  Menu Distribution
                </Typography>
                <ResponsiveContainer width="100%" height={300}>
                  <PieChart>
                    <Pie
                      data={menuDistribution}
                      cx="50%"
                      cy="50%"
                      labelLine={false}
                      label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                      outerRadius={80}
                      fill="#8884d8"
                      dataKey="value"
                    >
                      {menuDistribution.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                      ))}
                    </Pie>
                    <RechartsTooltip />
                  </PieChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          </motion.div>
        </Grid>

        {/* Inventory Status */}
        <Grid item xs={12} md={6}>
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5, duration: 0.5 }}
          >
            <Card className="card-hover">
              <CardContent>
                <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Inventory />
                  Inventory Status
                </Typography>
                <Box sx={{ mt: 2 }}>
                  {inventoryStatus.map((status, index) => (
                    <Box key={status.status} sx={{ mb: 2 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          {status.status}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {status.count} items
                        </Typography>
                      </Box>
                      <LinearProgress
                        variant="determinate"
                        value={(status.count / analytics.inventory.length) * 100}
                        color={status.status === 'Expired' ? 'error' : status.status === 'Expiring Soon' ? 'warning' : 'success'}
                        sx={{ height: 8, borderRadius: 4 }}
                      />
                    </Box>
                  ))}
                </Box>
              </CardContent>
            </Card>
          </motion.div>
        </Grid>
      </Grid>

      {/* Recent Activity Table */}
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.6, duration: 0.5 }}
      >
        <Card className="card-hover">
          <CardContent>
            <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <PeopleAlt />
              Recent Predictions
            </Typography>
            <TableContainer component={Paper} sx={{ mt: 2 }}>
              <Table>
                <TableHead>
                  <TableRow sx={{ backgroundColor: 'primary.main' }}>
                    <TableCell sx={{ color: 'white', fontWeight: 600 }}>Date</TableCell>
                    <TableCell sx={{ color: 'white', fontWeight: 600 }}>Location</TableCell>
                    <TableCell sx={{ color: 'white', fontWeight: 600 }}>Menu</TableCell>
                    <TableCell sx={{ color: 'white', fontWeight: 600 }}>Guests</TableCell>
                    <TableCell sx={{ color: 'white', fontWeight: 600 }}>Items</TableCell>
                    <TableCell sx={{ color: 'white', fontWeight: 600 }}>Status</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {analytics.predictions.slice(-10).map((prediction, index) => (
                    <TableRow key={prediction.id} sx={{ '&:nth-of-type(odd)': { backgroundColor: 'action.hover' } }}>
                      <TableCell>
                        {new Date(prediction.timestamp).toLocaleDateString()}
                      </TableCell>
                      <TableCell>{prediction.location}</TableCell>
                      <TableCell>
                        <Chip label={prediction.menu_name} size="small" color="primary" />
                      </TableCell>
                      <TableCell>{prediction.guests}</TableCell>
                      <TableCell>{Object.keys(prediction.menu_items || {}).length}</TableCell>
                      <TableCell>
                        <Chip
                          label="Completed"
                          size="small"
                          color="success"
                          variant="outlined"
                        />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>
      </motion.div>
    </Box>
  );
};

export default Analytics; 